import { ErrorHandler, Injectable, inject } from '@angular/core';
import type { SupabaseClient } from '@supabase/supabase-js';
import { AuthStore } from '../../auth.store';
import { BrandSlug, prettyBrandSlug } from '../shared/brand-slug';
import { type ChunkProgressReporter, reportChunkProgress } from '../shared/chunk-progress';
import type { EAN } from '../shared/ean';
import { fromMasked, toMasked } from '../shared/masked-ean';
import { Brand } from './brand';
import { BrandRepository } from './brand-repository';

/** Keys per `.in()` filter: the URL is a GET, so the whole key list rides in the query string. */
const KEY_BATCH_SIZE = 200;

/**
 * Batches in flight at once. A 28k-SKU brand is ~280 batches, so run serially the read would
 * be ~280 round trips deep; the availability ACL splits the same query string the same way.
 */
const MAX_CONCURRENT_BATCHES = 5;

function chunk<T>(values: readonly T[], size: number): readonly (readonly T[])[] {
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
}

@Injectable()
export class SupabaseBrandRepository extends BrandRepository {
  private readonly authStore = inject(AuthStore);
  private readonly errorHandler = inject(ErrorHandler);

  private client(): Promise<SupabaseClient> {
    return this.authStore.getSupabaseClient();
  }

  async getBySlug(slug: BrandSlug): Promise<Brand | null> {
    const supabase = await this.client();
    const { data } = await supabase
      .from('brandb')
      .select('brandname, brandalias')
      .eq('brandname', slug)
      .maybeSingle();
    if (!data) return null;
    return rowToBrand(data.brandname, data.brandalias);
  }

  async lookupMany(slugs: readonly BrandSlug[]): Promise<ReadonlyMap<BrandSlug, Brand>> {
    if (slugs.length === 0) return new Map();
    const supabase = await this.client();
    const { data } = await supabase
      .from('brandb')
      .select('brandname, brandalias')
      .in('brandname', Array.from(slugs));
    const out = new Map<BrandSlug, Brand>();
    for (const row of data ?? []) {
      const brand = rowToBrand(row.brandname, row.brandalias);
      out.set(brand.slug, brand);
    }
    return out;
  }

  async brandsOfBatch(eans: readonly EAN[]): Promise<ReadonlyMap<EAN, BrandSlug>> {
    if (eans.length === 0) return new Map();
    const supabase = await this.client();
    const { data } = await supabase
      .from('medpdb2')
      .select('EAN, brand_slug')
      .in('EAN', eans.map(toMasked));
    const out = new Map<EAN, BrandSlug>();
    let skipped = 0;
    for (const row of (data ?? []) as { EAN: string; brand_slug: string | null }[]) {
      if (!row.brand_slug) continue;
      const ean = fromMasked(String(row.EAN));
      if (ean) out.set(ean, BrandSlug(row.brand_slug));
      else skipped++;
    }
    this.reportUnstoredKeys('brandsOfBatch', skipped);
    return out;
  }

  async thumbnailsOf(
    eans: readonly EAN[],
    onProgress?: ChunkProgressReporter
  ): Promise<ReadonlyMap<EAN, string | null>> {
    if (eans.length === 0) return new Map();

    const supabase = await this.client();
    const batches = chunk(eans.map(toMasked), KEY_BATCH_SIZE);
    const out = new Map<EAN, string | null>();
    let skipped = 0;

    for (let i = 0; i < batches.length; i += MAX_CONCURRENT_BATCHES) {
      const slice = batches.slice(i, i + MAX_CONCURRENT_BATCHES);
      const wave = await Promise.all(slice.map((batch) => this.readThumbnails(supabase, batch)));
      reportChunkProgress(onProgress, (i + slice.length) * KEY_BATCH_SIZE, eans.length);
      // One failed batch makes the whole answer partial, and a partial set reads as
      // "these products have no picture"; the caller keeps what it already had instead.
      for (const rows of wave) {
        if (rows === null) return new Map();
        for (const row of rows) {
          const ean = fromMasked(String(row.EAN));
          if (ean) out.set(ean, row.img);
          else skipped++;
        }
      }
    }
    this.reportUnstoredKeys('thumbnailsOf', skipped);
    return out;
  }

  /**
   * One report per read, never per row: a systemic key-format break in medpdb2 would
   * otherwise be one Sentry event per affected product.
   */
  private reportUnstoredKeys(method: string, count: number): void {
    if (count === 0) return;
    this.errorHandler.handleError(
      new Error(
        `[BrandRepository] ${method}: ${count} medpdb2 row(s) carry a product key that is not a masked EAN; skipped`
      )
    );
  }

  /**
   * One request per batch, unpaged: medpdb2 holds one row per key, so a batch of at most
   * KEY_BATCH_SIZE keys cannot reach PostgREST's 1000-row page. Returns null once it errored.
   */
  private async readThumbnails(
    supabase: SupabaseClient,
    keys: readonly string[]
  ): Promise<{ EAN: string; img: string | null }[] | null> {
    const { data, error } = await supabase.from('medpdb2').select('EAN, img').in('EAN', keys);

    if (error) {
      this.errorHandler.handleError(
        new Error(`[BrandRepository] thumbnail read of medpdb2 failed: ${error.message}`)
      );
      return null;
    }
    return (data ?? []) as { EAN: string; img: string | null }[];
  }
}

function rowToBrand(brandname: string, brandalias: string | null): Brand {
  const slug = BrandSlug(brandname);
  const displayName = brandalias?.trim() || prettyBrandSlug(slug);
  return Brand({ slug, displayName });
}
